import { Trans, t } from '@lingui/macro';
import { useState } from "react";
import { BotMessage } from "../../../models/BotMessage";
import { BotPromptSuggestion } from "../../../models/BotPromptSuggestion";
import { UUID } from "../../../models/local/Uid";
import { Fire } from "../../../services/FireClient";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { useToast } from "../../../hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Label } from "../../../components/ui/label";

export const SuggestionFormModal: React.FC<{ bm: BotMessage }> = ({ bm }) => {
  const [show, setShow] = useState(false);
  const [isExisting, setIsExisting] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [newAnswer, setNewAnswer] = useState<string | null>(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [sug, setSug] = useState<BotPromptSuggestion>();
  const { toast } = useToast();

  async function onPresent() {
    let existingSug: BotPromptSuggestion | undefined;
    if (bm.suggestionId) {
      setInProgress(true);
      existingSug = await Fire.bot.getBotPromptSuggestion(bm.soribotId, bm.suggestionId);
      setInProgress(false);
      if (!existingSug) {
        toast({ description: t`Suggestion not found. Press Save to create a new one.` });
      } else {
        setSug(existingSug);
        setNewQuestion(existingSug.prompt);
        setNewAnswer(existingSug.response);
      }
    }
    if (!existingSug) {
      setSug(new BotPromptSuggestion(UUID.get(), bm.soribotId, bm.userPrompt, bm.llmResponse));
      setNewQuestion(bm.userPrompt);
      setNewAnswer(bm.llmResponse ?? '');
    }
    setIsExisting(!!existingSug);
  }

  const handleOpenChange = (open: boolean) => {
    setShow(open);
    if (open) {
      onPresent();
    }
  };

  return (<>
    <div className="flex justify-end p-2">
      <Button variant="ghost" size="sm" onClick={() => setShow(true)}>
        {bm.suggestionId ? <Trans>Edit this suggestion</Trans> : <Trans>Save as a suggestion</Trans>}
      </Button>
    </div>

    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{isExisting ? <Trans>Edit Suggestion</Trans> : <Trans>Create new suggestion</Trans>}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {inProgress && <div className="flex justify-center"><Loader2 className="animate-spin" /></div>}

          <div className="grid gap-2">
            <Label htmlFor="question"><Trans>Question</Trans></Label>
            <Textarea
              id="question"
              value={newQuestion}
              onChange={e => setNewQuestion(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="answer"><Trans>Fixed answer</Trans></Label>
            <Textarea
              id="answer"
              value={newAnswer ?? ''}
              onChange={e => setNewAnswer(e.target.value)}
              className="min-h-[150px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShow(false)} disabled={inProgress}><Trans>Cancel</Trans></Button>
          <Button onClick={saveChanges} disabled={inProgress}>{isExisting ? <Trans>Save changes</Trans> : <Trans>Save suggestion</Trans>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);

  async function saveChanges() {
    if (sug) {
      sug.prompt = newQuestion;
      sug.response = newAnswer;
      setInProgress(true);
      await Fire.bot.saveSuggestion(sug);
      setInProgress(false);
      const msg = isExisting ? t`Suggestion has been updated` : t`Suggestion has been created. You can find it in the Suggestions page.`
      toast({ description: msg });
    }
    setShow(false);
  }
}
